import { uniqBy } from 'lodash-es'
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAsyncFn } from 'react-use'
import { isMatching, P } from 'ts-pattern'
import type { PageInfoFragment } from 'src/services/apollo/generated/graphql'
import type { AnyFn } from './type'

type PageArgs =
  | {
      before?: string
      last: number
    }
  | {
      after?: string
      first: number
    }

/**
 * Get pagination arguments for relay connection
 */
export function getPaginationArgs(opts?: {
  pageSize?: number
  /**
   * The actual order of the data
   *
   * For recent-first lists,
   * the first page is actually the last page of the data in DB.
   * So when we request for the next page,
   * it is actually the previous page to be retrieved from the DB.
   * `dataOrder: "desc"` handles this case for you by transforming the
   * prev/next args (which is the default).
   * Pass `asc` if you do not need the transformation.
   */
  dataOrder?: 'asc' | 'desc'
}) {
  const { pageSize: _size = 30, dataOrder = 'desc' } = opts ?? {}
  const prevDataArgs = (pageInfo?: PageInfoFragment, pageSize = _size) =>
    !pageInfo || pageInfo.hasPreviousPage
      ? {
          before: pageInfo?.startCursor,
          last: pageSize,
        }
      : undefined
  const nextDataArgs = (pageInfo?: PageInfoFragment, pageSize = _size) =>
    !pageInfo || pageInfo.hasNextPage
      ? {
          after: pageInfo?.endCursor,
          first: pageSize,
        }
      : undefined

  return dataOrder === 'asc'
    ? {
        prevArgs: prevDataArgs,
        nextArgs: nextDataArgs,
      }
    : {
        prevArgs: nextDataArgs,
        nextArgs: prevDataArgs,
      }
}

export function usePaginationArgs(opts?: {
  pageSize?: number
  dataOrder?: 'asc' | 'desc'
}) {
  const dataOrder = opts?.dataOrder ?? 'desc'

  const pageInfoRef = useRef<PageInfoFragment>(null)
  const { prevArgs, nextArgs } = useMemo(
    () => getPaginationArgs(opts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [opts?.pageSize, opts?.dataOrder],
  )
  const [pageArgs, setPageArgs] = useState<PageArgs | undefined>(nextArgs())

  return {
    pageArgs,
    prevPage: () => setPageArgs(prevArgs(pageInfoRef.current ?? undefined)),
    nextPage: () => setPageArgs(nextArgs(pageInfoRef.current ?? undefined)),
    setPageInfo: (pageInfo: PageInfoFragment | undefined) => {
      pageInfoRef.current = pageInfo ?? null
    },
    hasPrevPage: useCallback(
      () =>
        dataOrder === 'asc'
          ? Boolean(pageInfoRef.current?.hasPreviousPage)
          : Boolean(pageInfoRef.current?.hasNextPage),
      [dataOrder],
    ),
    hasNextPage: useCallback(
      () =>
        dataOrder === 'asc'
          ? Boolean(pageInfoRef.current?.hasNextPage)
          : Boolean(pageInfoRef.current?.hasPreviousPage),
      [dataOrder],
    ),
  }
}

type GetItem<Data> = (
  data: Data,
) => Data extends Array<infer Item> ? Item : never

export function usePaginatedData<
  Fetcher extends AnyFn,
  Args = Parameters<Fetcher>[0],
  Data = Awaited<ReturnType<Fetcher>>,
  Item = GetItem<Data>,
>(opts: {
  fetcher: Fetcher
  getArgs: (data: Data | null) => Args
  getItems: (data: Data) => Item[]
  getHasMore: (data: Data) => boolean | undefined
  getTotalCount?: (data: Data) => number | undefined
  customState?: {
    items: Item[]
    /**
     * State handler compatible with React.useState, e.g. `jotai`
     */
    setItems: Dispatch<SetStateAction<Item[]>>
  }
}) {
  const { fetcher, getArgs, getItems, customState } = opts
  // getArgs may change as arguments might be updated
  const getArgsRef = useRef(getArgs)
  getArgsRef.current = getArgs
  // getItems may change because we may perform some front-end filtering
  const getItemsRef = useRef(getItems)
  getItemsRef.current = getItems

  const [localItems, localItemsSetter] = useState<Item[]>([])
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined)
  const items = customState?.items ?? localItems
  const setItems = customState?.setItems ?? localItemsSetter

  const lastData = useRef<Data | null>(null)
  const [hasMore, setHasMore] = useState(true)

  const [{ loading }, loadMore] = useAsyncFn(async () => {
    if (loading) return

    const args = getArgsRef.current(lastData.current)
    if (!args) return false
    const data: Data = await fetcher(args)
    lastData.current = data
    setItems(prevItems =>
      uniqBy([...prevItems, ...getItemsRef.current(data)], 'id'),
    )
    const hasMore = Boolean(opts.getHasMore(data))
    setHasMore(hasMore)

    // get total count
    if (opts.getTotalCount) {
      if (totalCount !== opts.getTotalCount(data)) {
        setTotalCount(opts.getTotalCount(data))
      }
    }

    return hasMore
  }, [fetcher])

  const reset = useCallback(() => {
    lastData.current = null
    setHasMore(true)
    setItems([])
  }, [setItems])

  return { items, setItems, hasMore, loading, loadMore, reset, totalCount }
}

export function useWindowedList<T>(
  items: T[],
  opts: {
    windowSize?: number
    getId?: (item: T) => string
    onReachStart?: () => void
    onReachEnd?: () => void
  },
) {
  const {
    windowSize = 20,
    getId = it => (isMatching({ id: P.string }, it) ? it.id : ERR_NO_ID()),
    onReachStart,
    onReachEnd,
  } = opts
  const maxIdx = items.length - 1

  const [start, setStart] = useState<string>()
  const startIdx = useMemo(
    () => (start ? items.findIndex(it => getId(it) === start) : 0),
    [getId, items, start],
  )
  const endIdx = Math.min(maxIdx, startIdx + windowSize - 1)

  useEffect(() => {
    if (startIdx === 0) {
      onReachStart?.()
    }
    if ((windowSize === 1 || endIdx > 0) && endIdx === maxIdx) {
      onReachEnd?.()
    }
  }, [
    items.length,
    windowSize,
    startIdx,
    endIdx,
    maxIdx,
    onReachStart,
    onReachEnd,
  ])

  const data = useMemo(
    () => items.slice(startIdx, endIdx + 1),
    [items, startIdx, endIdx],
  )

  function prev() {
    const prev = items[Math.max(0, startIdx - windowSize)]
    if (prev) {
      setStart(getId(prev))
    }
  }

  function next() {
    const next = items[endIdx + 1]
    if (next) {
      setStart(getId(next))
    }
  }

  return {
    data,
    havingPrev: startIdx > 0,
    havingNext: endIdx < maxIdx,
    prev,
    next,
    reset: () => setStart(undefined),
    startIdx,
    endIdx,
  }
}

function ERR_NO_ID(): never {
  throw new Error('Cannot access `id`')
}

/**
 * Compute the list of page numbers (and ellipsis markers) to display in a
 * pagination control.
 *
 * - Page 1 and `totalPages` are always shown.
 * - A window of ±2 around `page` is always shown.
 * - Gaps between the fixed anchors and the window are replaced by `'ellipsis'`
 *   only when the gap is more than one page wide (i.e. inserting an ellipsis
 *   saves at least one button slot).
 */
export function getPaginationPages(
  page: number,
  totalPages: number,
): (number | 'ellipsis')[] {
  const windowStart = Math.max(1, page - 2)
  const windowEnd = Math.min(totalPages, page + 2)

  const pages: (number | 'ellipsis')[] = []

  if (windowStart > 1) {
    pages.push(1)
    if (windowStart > 2) pages.push('ellipsis')
  }

  for (let i = windowStart; i <= windowEnd; i++) {
    pages.push(i)
  }

  if (windowEnd < totalPages) {
    if (windowEnd < totalPages - 1) pages.push('ellipsis')
    pages.push(totalPages)
  }

  return pages
}
